/**
 * Variant Image Filtering (Hybrid Approach)
 * ==========================================
 * Shows only the images belonging to the selected color variant.
 *
 * How it works:
 *   1. Fetches /products/{handle}.json to get variant_ids per image.
 *   2. Images WITH variant_ids are mapped directly to their color.
 *   3. Images WITHOUT variant_ids are grouped with the preceding
 *      assigned color (hybrid/position-based fallback).
 *   4. On color swatch click, hides non-matching images.
 */
(function () {
  'use strict';

  function initVariantImageFilter() {
    var productSection = document.querySelector('[data-section-type="product"]');
    if (!productSection) return;

    var productJsonEl = productSection.querySelector('[data-product-json]');
    if (!productJsonEl || !productJsonEl.innerHTML.trim()) return;

    var product;
    try {
      product = JSON.parse(productJsonEl.innerHTML);
    } catch (e) {
      return;
    }

    if (!product || !product.variants || !product.options || !product.handle) return;

    /* ── 1. Find the color option index ─────────────────────────── */
    var colorOptionIndex = -1;
    var colorNames = ['color', 'colour', 'colors', 'colours'];

    for (var i = 0; i < product.options.length; i++) {
      var optName = typeof product.options[i] === 'string'
        ? product.options[i]
        : (product.options[i].name || '');
      if (colorNames.indexOf(optName.toLowerCase()) !== -1) {
        colorOptionIndex = i;
        break;
      }
    }

    if (colorOptionIndex === -1) return;

    /* ── 2. Build variant_id → color map from page JSON ────────── */
    var vidToColor = {};
    for (var v = 0; v < product.variants.length; v++) {
      vidToColor[product.variants[v].id] = product.variants[v].options[colorOptionIndex];
    }

    /* ── 3. Fetch AJAX API to get variant_ids per image ─────────── */
    fetch('/products/' + product.handle + '.json')
      .then(function (res) { return res.json(); })
      .then(function (data) {
        var ajaxImages = data.product.images;

        /* ── 4. Build image_id → color using hybrid approach ───── */
        // Sort images by position
        ajaxImages.sort(function (a, b) { return a.position - b.position; });

        var imageIdToColor = {};  // image_id → color_value
        var lastAssignedColor = null;

        for (var i = 0; i < ajaxImages.length; i++) {
          var img = ajaxImages[i];
          var vids = img.variant_ids || [];

          if (vids.length > 0) {
            // Image is assigned to variants — use direct mapping
            var color = vidToColor[vids[0]];
            if (color) {
              imageIdToColor[img.id] = color;
              lastAssignedColor = color;
            }
          } else if (lastAssignedColor) {
            // Unassigned image — inherit from last assigned color
            imageIdToColor[img.id] = lastAssignedColor;
          }
          // If no color assigned yet, image stays unmapped (always visible)
        }



        /* ── 5. Build color → set of image IDs ─────────────────── */
        var colorImageIds = {};  // color → [image_id, ...]
        var allMappedIds = {};   // image_id → true

        for (var imgId in imageIdToColor) {
          var c = imageIdToColor[imgId];
          if (!colorImageIds[c]) colorImageIds[c] = [];
          colorImageIds[c].push(imgId.toString());
          allMappedIds[imgId.toString()] = true;
        }



        /* ── 6. Get DOM elements ───────────────────────────────── */
        var slides = Array.prototype.slice.call(
          productSection.querySelectorAll('[data-media-slide]')
        );
        var thumbHolder = productSection.querySelector('[data-product-thumbs]');
        var thumbs = thumbHolder
          ? Array.prototype.slice.call(thumbHolder.querySelectorAll('[data-slideshow-thumbnail]'))
          : [];

        if (slides.length === 0) return;

        // Map media-id to Shopify image ID
        // The DOM may contain videos and other non-image media that
        // the AJAX /products/{handle}.json images array does not include.
        // So we CANNOT use simple position-based mapping.
        // Instead, match by normalised image src URL.

        // Build a normalised src → image_id map from AJAX images
        function normaliseSrc(url) {
          if (!url) return '';
          // Strip protocol, query string, and size suffix
          url = url.replace(/^https?:/, '').split('?')[0].replace(/_\d+x\d*\./, '.');
          // Extract just the filename (CDN hostnames differ between API and DOM)
          return url.split('/').pop();
        }

        var srcToImageId = {};
        for (var p = 0; p < ajaxImages.length; p++) {
          var key = normaliseSrc(ajaxImages[p].src);
          srcToImageId[key] = ajaxImages[p].id.toString();
        }

        // Map each DOM slide to its AJAX image ID via src matching
        var slideImageIds = [];
        for (var s = 0; s < slides.length; s++) {
          var imgEl = slides[s].querySelector('img');
          if (imgEl) {
            var slideSrc = normaliseSrc(imgEl.src || imgEl.getAttribute('data-src') || imgEl.getAttribute('data-photoswipe-src') || '');
            slideImageIds.push(srcToImageId[slideSrc] || null);
          } else {
            // Non-image media (video, 3D model) — no match
            slideImageIds.push(null);
          }
        }

        // Assign non-image media (videos) to the preceding image's color
        // group so they're shown with the default color, not always visible.
        var slideIsVideo = [];
        for (var vi = 0; vi < slides.length; vi++) {
          var isVideo = !slideImageIds[vi] && slides[vi].getAttribute('data-type') !== 'image';
          slideIsVideo.push(isVideo);

          if (isVideo) {
            // Find the nearest preceding image ID that's mapped
            for (var prev = vi - 1; prev >= 0; prev--) {
              if (slideImageIds[prev] && imageIdToColor[slideImageIds[prev]]) {
                // Create a synthetic mapping: use the same color as preceding image
                var inheritedColor = imageIdToColor[slideImageIds[prev]];
                var syntheticId = '__video_' + vi;
                slideImageIds[vi] = syntheticId;
                imageIdToColor[syntheticId] = inheritedColor;
                if (!colorImageIds[inheritedColor]) colorImageIds[inheritedColor] = [];
                colorImageIds[inheritedColor].push(syntheticId);
                allMappedIds[syntheticId] = true;
                break;
              }
            }
          }
        }



        /* ── 7. The filter function ────────────────────────────── */
        function filterImages(selectedColor) {
          var allowedIds = colorImageIds[selectedColor] || [];
          var allowedSet = {};
          for (var a = 0; a < allowedIds.length; a++) {
            allowedSet[allowedIds[a]] = true;
          }

          var visibleMediaIds = {};
          var firstVisibleSlide = null;

          slides.forEach(function (slide, index) {
            var imgId = slideImageIds[index];

            // Remove hero class from all slides
            slide.classList.remove('variant-hero');

            if (!imgId || !allMappedIds[imgId]) {
              // Unmapped image — always visible (shared)
              slide.classList.remove('hide');
              visibleMediaIds[slide.getAttribute('data-media-id')] = true;
              if (!firstVisibleSlide && !slideIsVideo[index]) firstVisibleSlide = slide;
            } else if (allowedSet[imgId]) {
              // Belongs to selected color — show
              slide.classList.remove('hide');
              visibleMediaIds[slide.getAttribute('data-media-id')] = true;
              if (!firstVisibleSlide && !slideIsVideo[index]) firstVisibleSlide = slide;
            } else {
              // Belongs to different color — hide
              slide.classList.add('hide');
            }
          });

          // Mark the first visible slide as hero (full-width)
          if (firstVisibleSlide) {
            firstVisibleSlide.classList.add('variant-hero');
          }

          // Sync thumbnails
          thumbs.forEach(function (thumb) {
            var mid = thumb.getAttribute('data-media-select');
            if (visibleMediaIds[mid]) {
              thumb.classList.remove('hide');
            } else {
              thumb.classList.add('hide');
            }
          });

          // Reload Flickity if present
          var slideshowEl = productSection.querySelector('[data-product-slideshow]');
          if (slideshowEl && slideshowEl.classList.contains('flickity-enabled') && typeof Flickity !== 'undefined') {
            var flkty = Flickity.data(slideshowEl) || (window.FlickityFade && FlickityFade.data(slideshowEl));
            if (flkty) {
              flkty.reloadCells();
              flkty.select(0);
            }
          }
        }

        /* ── 8. Skeleton overlay helpers ────────────────────────── */
        var slideshow = productSection.querySelector('[data-product-slideshow]');

        function showSkeleton() {
          if (!slideshow) return;
          // Remove any existing overlay
          var existing = slideshow.querySelector('.variant-skeleton-overlay');
          if (existing) existing.remove();

          var overlay = document.createElement('div');
          overlay.className = 'variant-skeleton-overlay';
          overlay.innerHTML = '<div class="skeleton-block"></div>';
          slideshow.appendChild(overlay);
        }

        function hideSkeleton() {
          if (!slideshow) return;
          var overlay = slideshow.querySelector('.variant-skeleton-overlay');
          if (overlay) {
            overlay.classList.add('fade-out');
            setTimeout(function () { overlay.remove(); }, 200);
          }
        }

        /* ── 9. Listen for variant changes ─────────────────────── */
        var lastScrollY = null;
        var lastColor = null;

        productSection.addEventListener('theme:variant:change', function (event) {
          var variant = event.detail.variant;
          if (!variant) return;
          var selectedColor = variant.options[colorOptionIndex];
          var colorChanged = selectedColor !== lastColor;
          lastColor = selectedColor;

          // Capture scroll position BEFORE theme scrolls
          lastScrollY = window.scrollY;

          // Show skeleton only on actual color change
          if (colorChanged) {
            showSkeleton();
          }

          setTimeout(function () {
            filterImages(selectedColor);

            // Restore scroll position to prevent auto-scroll
            if (lastScrollY !== null) {
              window.scrollTo(0, lastScrollY);
              lastScrollY = null;
            }

            // Fade out skeleton if we showed it
            if (colorChanged) {
              setTimeout(hideSkeleton, 80);
            }
          }, 0);
        });

        /* ── 9. Apply initial filter ───────────────────────────── */
        var urlParams = new URLSearchParams(window.location.search);
        var urlVariantId = urlParams.get('variant');
        var currentVariant;

        if (urlVariantId) {
          for (var vi = 0; vi < product.variants.length; vi++) {
            if (product.variants[vi].id.toString() === urlVariantId) {
              currentVariant = product.variants[vi];
              break;
            }
          }
        }
        if (!currentVariant) {
          for (var vj = 0; vj < product.variants.length; vj++) {
            if (product.variants[vj].available) {
              currentVariant = product.variants[vj];
              break;
            }
          }
          if (!currentVariant) currentVariant = product.variants[0];
        }

        if (currentVariant) {
          var initialColor = currentVariant.options[colorOptionIndex];
          setTimeout(function () { filterImages(initialColor); }, 300);
        }
      })
      .catch(function (err) {
        console.error('[VariantFilter] Failed to fetch product JSON:', err);
      });
  }
  /**
   * Move Kiwi Sizing chart link inline with SIZE label.
   * Retries a few times since the Kiwi app injects async.
   */
  function moveSizeChart(attempt) {
    attempt = attempt || 0;
    var sizeWrapper = document.querySelector('[data-select-label="Size"]');
    var kiwiChart = document.querySelector('.ks-chart-container');

    if (sizeWrapper && kiwiChart) {
      sizeWrapper.appendChild(kiwiChart);
    } else if (attempt < 10) {
      setTimeout(function () { moveSizeChart(attempt + 1); }, 300);
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initVariantImageFilter();
      moveSizeChart();
    });
  } else {
    initVariantImageFilter();
    moveSizeChart();
  }
})();

/**
 * Open UpCart Drawer on Add-to-Cart
 * ==================================
 * Intercepts the default "popdown notification" behavior and
 * lets the UpCart app handle opening its own cart drawer instead.
 *
 * UpCart listens for cart changes (via fetch/XHR interception on /cart/add.js).
 * We just need to suppress the native theme's popdown so it doesn't
 * conflict with UpCart's drawer.
 */
(function () {
  'use strict';

  function initCartDrawerOnAdd() {
    // Listen on document in the capturing phase.
    // The theme dispatches 'theme:cart:popdown' on the popdown element
    // with bubbles:true, so our capturing listener on document fires BEFORE
    // the theme's own listener on the element.
    document.addEventListener('theme:cart:popdown', function (e) {
      // Stop the event from reaching the theme's CartPopdown handler
      e.stopImmediatePropagation();
      e.stopPropagation();

      // Hide the popdown bar if it was already visible
      var popdown = document.querySelector('[data-product-add-popdown-wrapper]');
      if (popdown) {
        popdown.classList.remove('is-visible');
      }

      // Fetch the latest cart state and broadcast the change.
      // UpCart picks up on cart state changes to auto-open its drawer.
      fetch(window.theme.routes.cart + '.js', {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' }
      })
        .then(function (res) { return res.json(); })
        .then(function (cart) {
          document.dispatchEvent(
            new CustomEvent('theme:cart:change', {
              detail: { cart: cart },
              bubbles: true
            })
          );

          // If UpCart doesn't auto-open from the cart change,
          // try clicking the cart icon to trigger it
          setTimeout(function () {
            var upcartOpen =
              document.querySelector('.upcart-icon') ||
              document.querySelector('[data-upcart-toggle]') ||
              document.querySelector('.upcart-trigger');

            if (upcartOpen) {
              upcartOpen.click();
            } else {
              // Fallback: try the Shopify cart icon which UpCart may have hijacked
              var cartIcon = document.querySelector('[data-drawer-toggle="drawer-cart"]');
              if (cartIcon) {
                cartIcon.click();
              }
            }
          }, 300);
        })
        .catch(function (err) {
          console.error('[SpicyWear] Cart drawer open error:', err);
        });
    }, true); // ← capturing = true, fires before target/bubble phase
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCartDrawerOnAdd);
  } else {
    initCartDrawerOnAdd();
  }
})();